import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  Observable,
  of,
  switchMap,
  map,
  combineLatest,
  take,
  Subscription,
  tap,
  catchError,
} from 'rxjs';
import { Store } from '@ngxs/store';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { TransferComponentForm } from './transfer-component.form';
import { ComponentsStructureState } from 'src/app/maintenance-admin/state/components-structure.state';
import {
  GetAllMaintenanceComponents,
  GetMaintenanceComponentsForDepartment,
  GetComponentTransferRoles,
} from 'src/app/maintenance-admin/state/components-structure.actions';
import { IdName } from 'src/app/shared/models/id-name';
import { DepartmentSelectOption } from 'src/app/shared/user-view/functions/enrich-select-options-with-department-pause';
import { TransferMaintenanceComponent } from 'src/app/maintenance-admin/state/components-structure.actions';
import { ComponentTransfer } from 'src/app/shared/models/component-transfer.model';
import { getDepartmentId } from 'src/app/services/store-snapshot.service';
import { mapDepartmentInfosToSelectOptions } from 'src/app/shared/user-view/functions/enrich-select-options-with-department-pause';
import { LoadAllJobPositionsForDepartmentAndGroups } from 'src/app/competence/state/job-position.actions';
import { LoadAllJobPositionGroupsForDepartmentAndGroups } from 'src/app/competence/state/job-position-group.actions';
import { ToastService } from 'src/app/core/services/toast.service';

export interface TransferComponentData {
  componentId: string;
  componentName: string;
  currentDepartmentId: string;
}

@Component({
  selector: 'app-transfer-component-modal',
  templateUrl: './transfer-component-modal.component.html',
  styleUrls: ['./transfer-component-modal.component.scss'],
  standalone: false,
})
export class TransferComponentModalComponent implements OnInit, OnDestroy {
  form = new TransferComponentForm();

  responsibleRoles$: Observable<IdName[]> = this.store.select(
    ComponentsStructureState.getComponentTransferResponsibleRoles
  );
  accessRoles$: Observable<IdName[]> = this.store.select(
    ComponentsStructureState.getComponentTransferAccessRoles
  );

  private subscriptions = new Subscription();

  allComponents$: Observable<IdName[]> = this.store
    .select(ComponentsStructureState.getAllComponentListForDepartment)
    .pipe(
      map((components) =>
        components
          .filter((c) => c.id !== '1' && c.name !== 'root')
          .map((c) => {
            // Fix "undefined" prefix issue - remove it if present
            let displayName = c.name;
            if (displayName.startsWith('undefined ')) {
              displayName = displayName.replace(/^undefined\s+/, '');
            }
            return {
              ...c,
              name: displayName,
            };
          })
      )
    );
  departments$: Observable<DepartmentSelectOption[]>;
  components$: Observable<IdName[]> = this.store
    .select(ComponentsStructureState.getComponentListForDepartment)
    .pipe(
      map((components) => {
        const rootOption: IdName = { id: '1', name: 'root' };
        return [rootOption, ...components];
      })
    );
  selectedDepartmentIds: string[] = [];

  constructor(
    public dialogRef: MatDialogRef<TransferComponentModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TransferComponentData,
    private fb: FormBuilder,
    private store: Store,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.loadAllComponents();
    this.initializeData();
    if (this.data.componentId) {
      this.loadTransferRoles(this.data.componentId);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onComponentChange(componentId: string | IdName): void {
    if (componentId) {
      const id = typeof componentId === 'string' ? componentId : componentId?.id;
      if (id) {
        this.loadTransferRoles(id);
      }
    }
  }

  onDepartmentChange(targetDepartmentId: string): void {
    if (!targetDepartmentId) {
      this.resetDependentFields();
      this.selectedDepartmentIds = [];
      return;
    }

    this.selectedDepartmentIds = [targetDepartmentId];
    this.loadComponentsForDepartment(targetDepartmentId);
    this.loadJobPositionsForDepartment(targetDepartmentId);
  }

  getResponsibleRoleGroup(roleId: string): any {
    return this.form.responsiblePositionsMap.get(roleId) as FormGroup;
  }

  getAccessRoleGroup(roleId: string): any {
    return this.form.accessPositionsMap.get(roleId) as FormGroup;
  }

  onTransfer(): void {
    if (this.form.valid) {
      this.subscriptions.add(
        combineLatest([this.responsibleRoles$, this.accessRoles$])
          .pipe(
            take(1),
            switchMap(([responsibleRoles, accessRoles]) => {
              const responsiblePositionsMap = this.buildPositionsMap(responsibleRoles, (roleId) =>
                this.getResponsibleRoleGroup(roleId)
              );

              const accessPositionsMap = this.buildPositionsMap(accessRoles, (roleId) =>
                this.getAccessRoleGroup(roleId)
              );

              const transfer: ComponentTransfer = {
                tenantId: this.form.value.tenantId || '',
                componentId: this.form.value.componentId || this.data.componentId,
                targetDepartmentId: this.form.value.targetDepartmentId || '',
                parentComponentId:
                  this.form.value.parentComponentId === '1'
                    ? null
                    : this.form.value.parentComponentId || null,
                accessPositionsMap: accessPositionsMap,
                responsiblePositionsMap: responsiblePositionsMap,
              };
              this.toast.success('componentTransferStarted');
              return this.store.dispatch(new TransferMaintenanceComponent(transfer));
            }),
            tap(() => {
              this.dialogRef.close();
            }),
            catchError(() => {
              this.toast.error('componentTransferFailed');
              return of(null);
            })
          )
          .subscribe()
      );
    }
  }

  private loadAllComponents(): void {
    this.store.dispatch(new GetAllMaintenanceComponents());
  }

  private loadTransferRoles(componentId?: string): void {
    if (componentId) {
      this.store.dispatch(new GetComponentTransferRoles(componentId));
    }
  }

  private initializeData(): void {
    if (this.data.componentId) {
      this.form.patchValue({ componentId: this.data.componentId });
    }

    const currentTenantId$ = this.store.select(TenantUserAccessState.currentTenantId);

    this.subscriptions.add(
      combineLatest([currentTenantId$, this.responsibleRoles$, this.accessRoles$]).subscribe(
        ([tenantId, responsibleRoles, accessRoles]) => {
          if (tenantId) {
            this.form.patchValue({ tenantId });
          }

          responsibleRoles.forEach((role) => {
            if (!this.form.responsiblePositionsMap.get(role.id)) {
              const roleGroup = this.form.createRoleMappingGroup();
              this.form.responsiblePositionsMap.addControl(role.id, roleGroup);
            }
          });

          accessRoles.forEach((role) => {
            if (!this.form.accessPositionsMap.get(role.id)) {
              const roleGroup = this.form.createRoleMappingGroup();
              this.form.accessPositionsMap.addControl(role.id, roleGroup);
            }
          });
        }
      )
    );

    this.departments$ = this.store.select(TenantUserAccessState.getAllAvailableDepartments).pipe(
      map((availableDepartments) => {
        const currentDepartmentId = getDepartmentId();
        return mapDepartmentInfosToSelectOptions(
          availableDepartments.filter(
            (dept) =>
              dept.id &&
              dept.id !== currentDepartmentId &&
              dept.id !== this.data.currentDepartmentId
          )
        );
      })
    );
  }

  private loadComponentsForDepartment(departmentId: string): void {
    this.store.dispatch(new GetMaintenanceComponentsForDepartment(departmentId));
    this.form.patchValue({ parentComponentId: null });
  }

  private loadJobPositionsForDepartment(departmentId: string): void {
    this.store.dispatch([
      new LoadAllJobPositionsForDepartmentAndGroups([departmentId], []),
      new LoadAllJobPositionGroupsForDepartmentAndGroups([departmentId], []),
    ]);
  }

  private resetDependentFields(): void {
    this.form.patchValue({ parentComponentId: null });

    Object.keys(this.form.responsiblePositionsMap.controls).forEach((key) => {
      const roleGroup = this.form.responsiblePositionsMap.get(key) as FormGroup;
      roleGroup.patchValue({ position: [], positionGroup: [] });
    });
    Object.keys(this.form.accessPositionsMap.controls).forEach((key) => {
      const roleGroup = this.form.accessPositionsMap.get(key) as FormGroup;
      roleGroup.patchValue({ position: [], positionGroup: [] });
    });
  }

  private buildPositionsMap(
    roles: IdName[],
    getRoleGroup: (roleId: string) => FormGroup | undefined
  ): Array<{ fromId: string; toId: string }> {
    return roles
      .map((role) => {
        const roleGroup = getRoleGroup(role.id);
        const positionValue = roleGroup?.get('position')?.value;
        const positionGroupValue = roleGroup?.get('positionGroup')?.value;

        let toId: string | null = null;

        if (positionValue) {
          if (typeof positionValue === 'string' && positionValue.trim() !== '') {
            toId = positionValue;
          } else if (Array.isArray(positionValue) && positionValue.length > 0) {
            toId = positionValue[0];
          }
        }

        if (!toId && positionGroupValue) {
          if (typeof positionGroupValue === 'string' && positionGroupValue.trim() !== '') {
            toId = positionGroupValue;
          } else if (Array.isArray(positionGroupValue) && positionGroupValue.length > 0) {
            toId = positionGroupValue[0];
          }
        }

        return {
          fromId: role.id,
          toId,
        };
      })
      .filter(
        (mapping): mapping is { fromId: string; toId: string } =>
          mapping.toId !== null && mapping.toId !== ''
      );
  }
}
